// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";

// interfaces
import { UpiPaymentMethod, IUpiPaymentGroup } from "@payment-ui/interfaces/Upi";
import { PaymentMethodProps } from "@payment-ui/interfaces";

// dls
import AddFilledIcon from "@swiggy-private/dls-web-icons/dist/web/generic/addFilled/12";

// widgets
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";
import Alert from "@payment-ui/widgets/alert/Alert";

// constants
import { TEXTS } from "@payment-ui/constants/Upi";

// test-ids
import { NEW_UPI_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

// helpers
import { canEnablePaymentMethod, getPaymentMethodPromoMsg } from "@payment-ui/helpers/Payment";
import { useEffectOnce } from "@swiggy-private/core-ui";

// styles
import { Container, PaymentMethod, IconContainer, Icon, AlertMessage } from "./styles/NewUpi.styled";

export interface NewUpiProps extends PaymentMethodProps {
    key: number;
    position: number;
    methodData: UpiPaymentMethod;
    paymentActions: IUpiPaymentGroup["paymentActions"];
    onClickAddNew: (value: string) => void;
    onImpressionAddNew: VoidFunction;
}

function NewUpiPaymentMethod(props: NewUpiProps): ReactElement {
    const promoMsg = getPaymentMethodPromoMsg(props.methodData);
    const isMethodEnabled = useMemo(() => canEnablePaymentMethod(props.methodData, props.isPaymentGroupEnabled), [
        props.isPaymentGroupEnabled,
        props.methodData,
    ]);
    const { onSelectAddNewVpa } = props.paymentActions;
    const { onClickAddNew, onImpressionAddNew } = props;

    useEffectOnce(() => {
        onImpressionAddNew();
    });

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            onClickAddNew(String(props.position));

            e.preventDefault();
            isMethodEnabled && onSelectAddNewVpa(props.methodData.name);
        },
        [onSelectAddNewVpa, props.methodData.name, isMethodEnabled, props.position, onClickAddNew],
    );

    return (
        <PaymentMethod $isDisabled={!isMethodEnabled}>
            <Container onClick={onClick} data-testid={NEW_UPI_PAYMENT_METHOD_TEST_IDS.CONTAINER}>
                <IconContainer>
                    <Icon
                        $isDisabled={!isMethodEnabled}
                        data-testid={NEW_UPI_PAYMENT_METHOD_TEST_IDS.ICON}
                        Icon={AddFilledIcon}
                        fillColor="primary"
                    />
                </IconContainer>
                <PaymentMethodName
                    displayName={TEXTS.ADD_NEW_UPI_ID}
                    promoMsg={promoMsg}
                    enabled={isMethodEnabled}
                    data-testid={NEW_UPI_PAYMENT_METHOD_TEST_IDS.NAME}
                    color="primary"
                >
                    {!isMethodEnabled && (
                        <AlertMessage>
                            <Alert
                                message={props.methodData.meta.disableMessage}
                                data-testid={NEW_UPI_PAYMENT_METHOD_TEST_IDS.DISABLE_MESSAGE}
                            />
                        </AlertMessage>
                    )}
                </PaymentMethodName>
            </Container>
        </PaymentMethod>
    );
}

export default memo(NewUpiPaymentMethod);
